/**
 * ContractorDashboard — a logged-in contractor's jobs/bids.
 *
 * Groups the contractor's bids by what they can do about them:
 *   - Action needed   (OFFERED, not expired)  → Accept / Counter / Decline inline
 *   - Awaiting owner   (ACCEPTED_BY_CONTRACTOR, COUNTERED)
 *   - Assigned         (ASSIGNED)
 *   - Closed           (DECLINED / EXPIRED / CLOSED_OTHER_ASSIGNED)
 *
 * The actions mirror the email page but are authorized by login. Staff-set
 * amounts are labor-only offers. See .kiro/specs/contractor-portal.md.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getMe,
  getContractorBids,
  acceptMyBid,
  counterMyBid,
  declineMyBid,
  getContractorBidProject,
  type ContractorBid,
  type ContractorProjectDetails,
} from '../lib/api';

function dollars(cents: number | null): string {
  if (cents == null) return '\u2014';
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

type Group = 'action' | 'awaiting' | 'assigned' | 'closed';

function groupOf(b: ContractorBid): Group {
  if (b.canRespond) return 'action';
  if (b.status === 'ACCEPTED_BY_CONTRACTOR' || b.status === 'COUNTERED') return 'awaiting';
  if (b.status === 'ASSIGNED') return 'assigned';
  return 'closed';
}

const GROUP_LABEL: Record<Group, string> = {
  action: 'Action needed',
  awaiting: 'Awaiting the owner',
  assigned: 'Assigned to you',
  closed: 'Closed',
};
const GROUP_ORDER: Group[] = ['action', 'awaiting', 'assigned', 'closed'];

const STATUS_LABEL: Record<string, string> = {
  OFFERED: 'New offer',
  ACCEPTED_BY_CONTRACTOR: 'You accepted — awaiting assignment',
  COUNTERED: 'You countered — awaiting the owner',
  ASSIGNED: 'Assigned to you',
  DECLINED: 'Declined',
  EXPIRED: 'Expired',
  CLOSED_OTHER_ASSIGNED: 'Closed (assigned elsewhere)',
};

export function ContractorDashboard(): JSX.Element {
  const navigate = useNavigate();
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [bids, setBids] = useState<ContractorBid[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [acting, setActing] = useState<string | null>(null);
  // Per-bid counter input (dollars string), only shown when "Counter" is toggled.
  const [counterOpen, setCounterOpen] = useState<Record<string, string>>({});
  // Per-bid lazy-loaded project details, keyed by bid id (null = loading).
  const [details, setDetails] = useState<Record<string, ContractorProjectDetails | null>>({});

  useEffect(() => {
    getMe()
      .then((me) => {
        const ok = me.role === 'CONTRACTOR';
        setAllowed(ok);
        if (ok) return getContractorBids().then(setBids);
        return undefined;
      })
      .catch(() => {
        setAllowed(false);
        setError('We couldn\u2019t load your jobs.');
      });
  }, []);

  async function refresh(): Promise<void> {
    setBids(await getContractorBids());
  }

  const groups = useMemo(() => {
    return GROUP_ORDER.map((g) => ({
      group: g,
      items: (bids ?? []).filter((b) => groupOf(b) === g),
    })).filter((g) => g.items.length > 0);
  }, [bids]);

  async function doAccept(id: string): Promise<void> {
    setActing(id);
    setError(null);
    try {
      await acceptMyBid(id);
      await refresh();
    } catch {
      setError('That didn\u2019t go through. Reload and try again.');
    } finally {
      setActing(null);
    }
  }

  async function doDecline(id: string): Promise<void> {
    setActing(id);
    setError(null);
    try {
      await declineMyBid(id);
      await refresh();
    } catch {
      setError('That didn\u2019t go through. Reload and try again.');
    } finally {
      setActing(null);
    }
  }

  async function doCounter(id: string): Promise<void> {
    const raw = (counterOpen[id] ?? '').trim();
    const cents = Math.round(Number(raw) * 100);
    if (!raw || Number.isNaN(cents) || cents <= 0) {
      setError('Enter a counter amount in dollars.');
      return;
    }
    setActing(id);
    setError(null);
    try {
      await counterMyBid(id, cents);
      setCounterOpen((m) => {
        const next = { ...m };
        delete next[id];
        return next;
      });
      await refresh();
    } catch {
      setError('That didn\u2019t go through. Reload and try again.');
    } finally {
      setActing(null);
    }
  }

  // Toggle the project-details panel for a bid; lazy-load the scope on first open.
  async function toggleDetails(bidId: string): Promise<void> {
    if (bidId in details) {
      setDetails((d) => {
        const next = { ...d };
        delete next[bidId];
        return next;
      });
      return;
    }
    setDetails((d) => ({ ...d, [bidId]: null })); // loading
    try {
      const data = await getContractorBidProject(bidId);
      setDetails((d) => ({ ...d, [bidId]: data }));
    } catch {
      setError('We couldn\u2019t load the project details. Try again.');
      setDetails((d) => {
        const next = { ...d };
        delete next[bidId];
        return next;
      });
    }
  }

  if (allowed === null) {
    return <div className="flow flow--single"><div className="stage-card"><p className="stage-card__lede">Checking access&hellip;</p></div></div>;
  }
  if (!allowed) {
    return (
      <div className="flow flow--single"><div className="stage-card">
        <h1>Not available</h1>
        <p className="stage-card__lede">This area is for contractors. If you were sent a bid by email, use the link in that email.</p>
        <div className="q-actions"><button type="button" className="pb-btn" onClick={() => navigate('/dashboard')}>Back</button></div>
      </div></div>
    );
  }

  return (
    <div className="flow flow--single">
      <div className="stage-card">
        <h1>My jobs</h1>
        <p className="stage-card__lede">Offers sent to you, and the jobs you&rsquo;ve been assigned.</p>

        {error ? <div className="pb-note pb-note--flag" role="alert">{error}</div> : null}

        {!bids ? (
          <p className="stage-card__lede">Loading&hellip;</p>
        ) : bids.length === 0 ? (
          <p className="stage-card__lede">No offers yet. When the owner bids a job out to you, it shows up here.</p>
        ) : (
          groups.map((g) => (
            <div key={g.group} style={{ marginTop: 'var(--pb-4)' }}>
              <div className={`dash-bucket__head${g.group === 'action' ? ' dash-bucket__head--review' : ''}`}>
                {GROUP_LABEL[g.group]} <span className="dash-bucket__count">{g.items.length}</span>
              </div>
              {g.items.map((b) => {
                const amount = b.acceptedAmountCents ?? b.counterAmountCents ?? b.offeredAmountCents;
                const showCounter = b.id in counterOpen;
                return (
                  <div key={b.id} className="pb-surface" style={{ marginBottom: 'var(--pb-2)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                      <span className="pb-option__label">{b.projectName ?? 'Project'}</span>
                      <span className="pb-option__label">{dollars(amount)}</span>
                    </div>
                    <div className="estimate__hint">
                      {b.roomLabels.join(', ') || 'Labor'} &middot; {STATUS_LABEL[b.status] ?? b.status}
                    </div>
                    {b.counterAmountCents != null && b.status === 'COUNTERED' ? (
                      <div className="estimate__hint">
                        You offered to do it for {dollars(b.counterAmountCents)} (owner offered {dollars(b.offeredAmountCents)}).
                      </div>
                    ) : null}

                    {/* See project details — scope of work per room + (soon) media.
                        A full-width expander row with a +/− so it reads as tappable
                        on mobile. */}
                    <div style={{ marginTop: 'var(--pb-2)' }}>
                      <button
                        type="button"
                        className="pb-expander"
                        aria-expanded={b.id in details}
                        onClick={() => void toggleDetails(b.id)}
                      >
                        <span>See project details</span>
                        <span className="pb-expander__icon" aria-hidden="true">
                          {b.id in details ? '\u2212' : '+'}
                        </span>
                      </button>
                      {b.id in details ? <BidDetails data={details[b.id] ?? null} /> : null}
                    </div>

                    {b.canRespond ? (
                      <div className="q-actions" style={{ marginTop: 'var(--pb-2)', flexWrap: 'wrap' }}>
                        <button type="button" className="pb-btn" disabled={acting === b.id} onClick={() => void doAccept(b.id)}>
                          Accept {dollars(b.offeredAmountCents)}
                        </button>
                        {showCounter ? (
                          <span style={{ display: 'flex', gap: 'var(--pb-1)', alignItems: 'center' }}>
                            <input
                              className="pb-field"
                              style={{ width: '8rem' }}
                              inputMode="decimal"
                              placeholder="Your price"
                              value={counterOpen[b.id] ?? ''}
                              onChange={(e) => setCounterOpen((m) => ({ ...m, [b.id]: e.target.value }))}
                              aria-label="Counter amount in dollars"
                            />
                            <button type="button" className="pb-btn" disabled={acting === b.id} onClick={() => void doCounter(b.id)}>
                              Send
                            </button>
                          </span>
                        ) : (
                          <button type="button" className="pb-btn pb-btn--ghost" onClick={() => setCounterOpen((m) => ({ ...m, [b.id]: '' }))}>
                            Counter
                          </button>
                        )}
                        <button type="button" className="pb-btn pb-btn--ghost" disabled={acting === b.id} onClick={() => void doDecline(b.id)}>
                          Decline
                        </button>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

/**
 * The scope-of-work breakdown for a bid's project: each room/bathroom with its
 * work items (no prices), plus a photos/videos section. Media capture isn't
 * built yet, so that section shows a placeholder for now.
 */
function BidDetails({ data }: { data: ContractorProjectDetails | null }): JSX.Element {
  if (!data) {
    return <p className="estimate__hint" style={{ marginTop: 'var(--pb-2)' }}>Loading details&hellip;</p>;
  }
  return (
    <div
      style={{
        padding: 'var(--pb-3)',
        border: 'var(--pb-border) solid var(--pb-chalk)',
        borderTop: 'none',
        borderBottomLeftRadius: 'var(--pb-radius-field)',
        borderBottomRightRadius: 'var(--pb-radius-field)',
      }}
    >
      <div className="dash-bucket__head">Scope of work</div>
      {data.rooms.length === 0 ? (
        <p className="estimate__hint">No scope detail available.</p>
      ) : (
        data.rooms.map((room, i) => (
          <div key={`${room.label}-${i}`} style={{ marginBottom: 'var(--pb-2)' }}>
            <div className="pb-option__label">
              {room.label} <span className="estimate__hint">({room.serviceLine})</span>
            </div>
            {room.scope.length === 0 ? (
              <div className="estimate__hint">Scope to be confirmed.</div>
            ) : (
              <ul style={{ margin: '4px 0 0', paddingLeft: 'var(--pb-4)' }}>
                {room.scope.map((item, j) => (
                  <li key={`${item.name}-${j}`} className="estimate__hint">
                    {item.name}
                    {item.quantity > 1 ? ` \u00d7 ${item.quantity}` : ''}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))
      )}

      <div className="dash-bucket__head" style={{ marginTop: 'var(--pb-3)' }}>Photos, videos &amp; links</div>
      {data.mediaAvailable && data.media.length > 0 ? (
        <div className="media-grid">
          {data.media.map((m, i) => (
            <div key={i} className="media-tile">
              {m.kind === 'image' && m.url ? (
                <a href={m.url} target="_blank" rel="noreferrer">
                  <img src={m.url} alt={m.label ?? 'Project photo'} className="media-tile__img" />
                </a>
              ) : m.kind === 'video' && m.url ? (
                <video src={m.url} controls className="media-tile__img" />
              ) : (
                <a href={m.url ?? '#'} target="_blank" rel="noreferrer" className="media-tile__link">
                  🔗 {m.label || m.url}
                </a>
              )}
              {m.label ? <div className="media-tile__row"><span className="estimate__hint">{m.label}</span></div> : null}
            </div>
          ))}
        </div>
      ) : (
        <p className="estimate__hint">
          No photos or videos attached yet. When the customer or owner adds
          job-site media, it&rsquo;ll show here so you can assess the work.
        </p>
      )}
    </div>
  );
}
